import type { Workbook, Worksheet } from 'exceljs';

// Parses the Excel export of a Xero "Profit and Loss" report.
// Tested against the format produced by Xero NZ as at April 2026:
// row 1 = "Profit and Loss", row 2 = org name, row 3 = date range,
// row 5 = header ("Account" + period columns), then alternating
// section headings, account rows ("CODE - NAME"), and section totals.

export type ParsedPLRow = {
  code: string;
  name: string;
  section: string;
  values: number[]; // one per period
};

export type ParsedPLTotal = {
  label: string; // e.g. "Total Trading Income", "Net Profit"
  values: number[];
};

export type ParsedPL = {
  orgName: string;
  reportTitle: string;
  dateRange: string;
  periods: string[];
  rows: ParsedPLRow[];
  totals: ParsedPLTotal[];
};

const ACCOUNT_LINE = /^\s*(\d+)\s*-\s*(.+?)\s*$/;

/** Coerces an ExcelJS cell value to number-or-null. Handles formula
 *  cells (`{ formula, result }`), rich-text strings, and direct numbers. */
function cellNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Xero sometimes formats (1,234.56) as a parenthesised negative.
    const cleaned = value.trim().replace(/,/g, '');
    const parens = /^\(([\d.]+)\)$/.exec(cleaned);
    if (parens) return -Number(parens[1]);
    const n = Number(cleaned);
    return isNaN(n) ? null : n;
  }
  if (typeof value === 'object') {
    // Formula cell → use its computed result.
    const obj = value as { result?: unknown; richText?: { text: string }[] };
    if (obj.result !== undefined) return cellNumber(obj.result);
    if (obj.richText) return cellNumber(obj.richText.map((t) => t.text).join(''));
  }
  return null;
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const obj = value as { result?: unknown; richText?: { text: string }[] };
    if (obj.richText)
      return obj.richText
        .map((t) => t.text)
        .join('')
        .trim();
    if (obj.result !== undefined) return cellText(obj.result);
  }
  return '';
}

export async function parseXeroPLFromBuffer(buffer: ArrayBuffer): Promise<ParsedPL> {
  const { Workbook: WorkbookImpl } = await import('exceljs');
  const wb = new WorkbookImpl() as Workbook;
  await wb.xlsx.load(buffer);
  const sheet = wb.worksheets[0];
  if (!sheet) throw new Error('No worksheet found in the uploaded file.');

  // Locate the header row first (first row whose column A is exactly
  // "Account"). Anything above it is metadata. This way we don't depend
  // on the title or org name sitting in fixed row positions.
  let headerRowNum = 0;
  for (let r = 1; r <= Math.min(sheet.rowCount, 30); r++) {
    if (cellText(sheet.getCell(r, 1).value).toLowerCase() === 'account') {
      headerRowNum = r;
      break;
    }
  }
  if (!headerRowNum) {
    // Surface what we actually saw so we can diagnose format drift.
    const peek: string[] = [];
    for (let r = 1; r <= Math.min(sheet.rowCount, 12); r++) {
      const a = cellText(sheet.getCell(r, 1).value);
      const b = cellText(sheet.getCell(r, 2).value);
      peek.push(`  ${r}: A="${a}" B="${b}"`);
    }
    throw new Error(
      `Could not find an "Account" header row in the first 30 rows of "${
        sheet.name
      }" (sheet has ${sheet.rowCount} rows × ${sheet.columnCount} cols).\n` +
        `First 12 rows:\n${peek.join('\n')}\n` +
        'If this looks correct, paste the message and the file structure to the developer.',
    );
  }

  // Pull title / org / date range from the metadata block above the header.
  // We scan upward for the first non-empty text cells and pick the
  // P&L title (if present) plus the next two non-empty rows for org + dates.
  const metaTexts: string[] = [];
  for (let r = 1; r < headerRowNum; r++) {
    const t = cellText(sheet.getCell(r, 1).value);
    if (t) metaTexts.push(t);
  }
  const titleIdx = metaTexts.findIndex((t) => /profit\s*and\s*loss/i.test(t));
  const reportTitle = metaTexts[titleIdx] ?? metaTexts[0] ?? 'Profit and Loss';
  const orgName = metaTexts.find((t, i) => i !== titleIdx && !/^for\b/i.test(t)) || '';
  const dateRange =
    metaTexts.find((t) => /^for\b/i.test(t)) || metaTexts[metaTexts.length - 1] || '';

  if (titleIdx < 0) {
    // Soft warning rather than a hard failure — log via console so dev tools
    // catch it but parsing proceeds. The Account header presence is the
    // real signal that this is a P&L (other reports use Account too, but
    // the worst case is we parse a Trial Balance and get sensible-but-mislabelled
    // data; the user can spot that in the preview).
    console.warn(
      'Xero P&L parser: no "Profit and Loss" title text found in metadata; proceeding based on Account header.',
    );
  }

  // Period labels from column 2 rightward, until an empty column.
  const periods: string[] = [];
  for (let c = 2; c <= sheet.columnCount; c++) {
    const t = cellText(sheet.getCell(headerRowNum, c).value);
    if (!t) break;
    periods.push(t);
  }
  if (periods.length === 0) {
    throw new Error('No period columns found alongside the Account header.');
  }

  // Walk the data rows.
  const rows: ParsedPLRow[] = [];
  const totalLabels: string[] = []; // collected during walk; values computed below
  let currentSection = '';

  for (let r = headerRowNum + 1; r <= sheet.rowCount; r++) {
    const label = cellText(sheet.getCell(r, 1).value);
    if (!label) continue;

    // Read period values for this row.
    const values: number[] = periods.map((_, i) => {
      const n = cellNumber(sheet.getCell(r, 2 + i).value);
      return n === null ? 0 : n;
    });

    const accountMatch = ACCOUNT_LINE.exec(label);
    if (accountMatch) {
      rows.push({
        code: accountMatch[1] ?? '',
        name: accountMatch[2] ?? '',
        section: currentSection,
        values,
      });
      continue;
    }

    // Totals / subtotals — record labels only; we compute values below
    // because Xero exports the formulas (SUM/etc) without cached results.
    if (
      /^total\b/i.test(label) ||
      /^gross profit\b/i.test(label) ||
      /^net profit\b/i.test(label) ||
      /^net loss\b/i.test(label) ||
      /^net income\b/i.test(label)
    ) {
      totalLabels.push(label);
      continue;
    }

    // Otherwise treat as a section heading (label only, no values).
    const hasAnyValue = values.some((v) => v !== 0);
    if (!hasAnyValue) {
      currentSection = label;
    }
  }

  // Compute totals from the row data. Section subtotals are easy
  // (sum rows where section matches). For derived bottom lines we
  // assume any section whose name contains "income"/"revenue" is on
  // the credit side and everything else is debit (expense).
  const isIncomeSection = (s: string): boolean => /\b(income|revenue|trading)\b/i.test(s);

  const sumRows = (predicate: (r: ParsedPLRow) => boolean): number[] =>
    periods.map((_, i) => rows.filter(predicate).reduce((acc, r) => acc + (r.values[i] || 0), 0));

  const totals: ParsedPLTotal[] = totalLabels.map((label) => {
    const subtotalMatch = /^total\s+(.+)$/i.exec(label);
    if (subtotalMatch) {
      const sectionName = (subtotalMatch[1] ?? '').trim();
      return {
        label,
        values: sumRows((r) => r.section.toLowerCase() === sectionName.toLowerCase()),
      };
    }
    if (/^gross profit\b/i.test(label)) {
      return { label, values: sumRows((r) => isIncomeSection(r.section)) };
    }
    if (/^net (profit|loss|income)\b/i.test(label)) {
      const income = sumRows((r) => isIncomeSection(r.section));
      const expense = sumRows((r) => !isIncomeSection(r.section));
      return { label, values: income.map((v, i) => v - (expense[i] ?? 0)) };
    }
    return { label, values: periods.map(() => 0) };
  });

  return { orgName, reportTitle, dateRange, periods, rows, totals };
}

// ─── Three-tab workbook (Steve's hand-crafted file) ─────────────────────
// Tab "Profit and Loss"               → FY{year} actuals
// Tab "Statement of Financial Perfo…" → Proj FY{year} + section grouping
// Tab "Church Income"                 → reserved data (raw cells)

export type ParsedChurchIncome = {
  sheetName: string;
  years: number[];
  churches: { name: string; byYear: Record<number, number> }[];
};

export type ParsedXlsx = {
  pAndL: ParsedPL | null;
  performance: ParsedPL | null;
  churchIncome: ParsedChurchIncome | null;
};

const findTab = (wb: Workbook, prefix: string): Worksheet | undefined =>
  wb.worksheets.find((s) => s.name.toLowerCase().startsWith(prefix.toLowerCase()));

async function parsePLSheet(sheet: Worksheet): Promise<ParsedPL> {
  // The "Account" header can be in column A (vanilla P&L export) or
  // column B (Xero's "Statement of Financial Performance (Line Item)"
  // export, which puts top-level Income/Expenses markers in A). Scan
  // the first few columns to locate it.
  let headerRowNum = 0;
  let labelCol = 1;
  for (let r = 1; r <= Math.min(sheet.rowCount, 30); r++) {
    for (let c = 1; c <= Math.min(sheet.columnCount, 4); c++) {
      if (cellText(sheet.getCell(r, c).value).toLowerCase() === 'account') {
        headerRowNum = r;
        labelCol = c;
        break;
      }
    }
    if (headerRowNum) break;
  }
  if (!headerRowNum) {
    const peek: string[] = [];
    for (let r = 1; r <= Math.min(sheet.rowCount, 12); r++) {
      const a = cellText(sheet.getCell(r, 1).value);
      const b = cellText(sheet.getCell(r, 2).value);
      peek.push(`  ${r}: A="${a}" B="${b}"`);
    }
    throw new Error(
      `Tab "${sheet.name}" — no "Account" header row found in first 30 rows.\n` +
        `First 12 rows:\n${peek.join('\n')}`,
    );
  }

  // Metadata: scan the first column from each row above the header
  // (Steve's SoFP file keeps the title/org/date in column A, even though
  // the data lives in column B).
  const metaTexts: string[] = [];
  for (let r = 1; r < headerRowNum; r++) {
    const t = cellText(sheet.getCell(r, 1).value) || cellText(sheet.getCell(r, labelCol).value);
    if (t) metaTexts.push(t);
  }
  const titleIdx = metaTexts.findIndex((t) =>
    /profit\s*and\s*loss|financial\s+performance/i.test(t),
  );
  const reportTitle = metaTexts[titleIdx] ?? metaTexts[0] ?? sheet.name;
  const orgName = metaTexts.find((t, i) => i !== titleIdx && !/^for\b/i.test(t)) || '';
  const dateRange =
    metaTexts.find((t) => /^for\b/i.test(t)) || metaTexts[metaTexts.length - 1] || '';

  // Periods are the columns to the right of the label column.
  const periods: string[] = [];
  for (let c = labelCol + 1; c <= sheet.columnCount; c++) {
    const t = cellText(sheet.getCell(headerRowNum, c).value);
    if (!t) break;
    periods.push(t);
  }
  if (periods.length === 0) {
    throw new Error(`Tab "${sheet.name}" — no period columns found alongside the Account header.`);
  }

  const rows: ParsedPLRow[] = [];
  const totalLabels: string[] = [];
  let currentSection = '';

  for (let r = headerRowNum + 1; r <= sheet.rowCount; r++) {
    const label = cellText(sheet.getCell(r, labelCol).value);
    if (!label) continue;

    const values: number[] = periods.map((_, i) => {
      const n = cellNumber(sheet.getCell(r, labelCol + 1 + i).value);
      return n === null ? 0 : n;
    });

    const accountMatch = ACCOUNT_LINE.exec(label);
    if (accountMatch) {
      rows.push({
        code: accountMatch[1] ?? '',
        name: accountMatch[2] ?? '',
        section: currentSection,
        values,
      });
      continue;
    }

    if (
      /^total\b/i.test(label) ||
      /^gross profit\b/i.test(label) ||
      /^net profit\b/i.test(label) ||
      /^net loss\b/i.test(label) ||
      /^net income\b/i.test(label)
    ) {
      totalLabels.push(label);
      continue;
    }

    const hasAnyValue = values.some((v) => v !== 0);
    if (!hasAnyValue) currentSection = label;
  }

  const isIncomeSection = (s: string): boolean => /\b(income|revenue|trading)\b/i.test(s);
  const sumRows = (predicate: (r: ParsedPLRow) => boolean): number[] =>
    periods.map((_, i) => rows.filter(predicate).reduce((acc, r) => acc + (r.values[i] || 0), 0));
  const totals: ParsedPLTotal[] = totalLabels.map((label) => {
    const subtotalMatch = /^total\s+(.+)$/i.exec(label);
    if (subtotalMatch) {
      const sectionName = (subtotalMatch[1] ?? '').trim();
      return {
        label,
        values: sumRows((r) => r.section.toLowerCase() === sectionName.toLowerCase()),
      };
    }
    if (/^gross profit\b/i.test(label)) {
      return { label, values: sumRows((r) => isIncomeSection(r.section)) };
    }
    if (/^net (profit|loss|income)\b/i.test(label)) {
      const income = sumRows((r) => isIncomeSection(r.section));
      const expense = sumRows((r) => !isIncomeSection(r.section));
      return { label, values: income.map((v, i) => v - (expense[i] ?? 0)) };
    }
    return { label, values: periods.map(() => 0) };
  });

  return { orgName, reportTitle, dateRange, periods, rows, totals };
}

function parseChurchIncomeSheet(sheet: Worksheet): ParsedChurchIncome | null {
  // Locate the header row by scanning the first ~10 rows for a row that
  // contains at least 2 four-digit-year cells. The leftmost year cell
  // tells us where the data columns start; one column to the left of
  // that is the church-name column.
  let headerRow = 0;
  let yearStartCol = 0;
  for (let r = 1; r <= Math.min(sheet.rowCount, 10); r++) {
    const yearCols: number[] = [];
    for (let c = 1; c <= sheet.columnCount; c++) {
      const t = cellText(sheet.getCell(r, c).value);
      if (/^\d{4}$/.test(t)) yearCols.push(c);
    }
    if (yearCols.length >= 2) {
      headerRow = r;
      yearStartCol = yearCols[0] ?? 0;
      break;
    }
  }
  if (!headerRow) return null;

  const years: number[] = [];
  for (let c = yearStartCol; c <= sheet.columnCount; c++) {
    const t = cellText(sheet.getCell(headerRow, c).value);
    if (/^\d{4}$/.test(t)) years.push(parseInt(t, 10));
    else break;
  }
  if (years.length === 0) return null;

  const nameCol = yearStartCol - 1;
  const churches: { name: string; byYear: Record<number, number> }[] = [];
  for (let r = headerRow + 1; r <= sheet.rowCount; r++) {
    const name = cellText(sheet.getCell(r, nameCol).value);
    if (!name) continue;
    const byYear: Record<number, number> = {};
    years.forEach((yr, i) => {
      const v = cellNumber(sheet.getCell(r, yearStartCol + i).value);
      if (typeof v === 'number') byYear[yr] = Math.round(v);
    });
    churches.push({ name, byYear });
  }

  return { sheetName: sheet.name, years, churches };
}

export async function parseXeroXlsx(file: File): Promise<ParsedXlsx> {
  const buffer = await file.arrayBuffer();
  return parseXeroXlsxFromBuffer(buffer);
}

export async function parseXeroXlsxFromBuffer(buffer: ArrayBuffer): Promise<ParsedXlsx> {
  const { Workbook: WorkbookImpl } = await import('exceljs');
  const wb = new WorkbookImpl() as Workbook;
  await wb.xlsx.load(buffer);

  const tab1 = findTab(wb, 'profit and loss');
  const tab2 = findTab(wb, 'statement of financial perfo') || findTab(wb, 'financial performance');
  const tab3 = findTab(wb, 'church income');

  // Backwards compatibility: if only one sheet and it's a P&L, treat it
  // as Tab 1. This keeps the old single-tab xlsx format working.
  let pAndL: ParsedPL | null = null;
  if (tab1) {
    pAndL = await parsePLSheet(tab1);
  } else if (wb.worksheets.length === 1) {
    const ws = wb.worksheets[0];
    if (ws) pAndL = await parsePLSheet(ws);
  }

  return {
    pAndL,
    performance: tab2 ? await parsePLSheet(tab2) : null,
    churchIncome: tab3 ? parseChurchIncomeSheet(tab3) : null,
  };
}
